import type {
  SeriesTemplateAttributes,
  SessionTemplateAttributes,
  WorldAttributes,
} from '@/server/models'

import ChannelQ from '@/queries/ChannelQ'
import { SQL, type SQLFragment } from '@/client/query'
import type { WorldVisibility } from '@/client/services/worlds'
import {
  COMMUNITY_WORLD_SLUGS,
  EDITABLE_POST_TYPES,
  PROGRAM_SUBWORLD_SLUGS,
  VISIBLE_WORLD_TYPES,
} from '@/client/services/worlds/constants'

import CourseQ from './CourseQ'
import {
  type AssociationDefinitions,
  type GroupBy,
  type IncludeAssociations,
  includeAssociationsToSql,
  makeQueryModelV2,
  type QueryOrder,
  queryGroupByToSQL,
  queryLimitToSQL,
  queryOrderToSQL,
} from './common'
import PostQ from './PostQ'
import SeriesTemplateQ from './SeriesTemplateQ'
import SessionTemplateQ from './SessionTemplateQ'
import TopicQ from './TopicQ'
import WorldMemberQ from './WorldMemberQ'

const WorldQ = {
  ...makeQueryModelV2<WorldAttributes>('world')([
    'id',
    'course_id',
    'title',
    'description',
    'guidelines',
    'slug',
    'icon_url',
    'primary_color',
    'visibility',
    'domain',
    'position',
    'created_at',
    'updated_at',
    'deleted_at',
  ]),

  associations: (): AssociationDefinitions => ({
    Course: {
      query: CourseQ,
      foreignKey: CourseQ.id,
      sourceKey: WorldQ.course_id,
    },
    WorldMembers: {
      query: WorldMemberQ,
      foreignKey: WorldMemberQ.world_id,
      sourceKey: WorldQ.id,
    },
  }),

  where: (
    {
      id = null,
      courseId = null,
      slug = null,
      visibility = null,
      include = [],
      limit = null,
      groupBy = null,
      order = null,
      paranoid = true,
    }: {
      id?: number | null | SQLFragment<number>
      courseId?: number | null | SQLFragment<number>
      slug?: string | null | SQLFragment<string>
      visibility?: WorldVisibility | null
      include?: IncludeAssociations
      limit?: number | null
      groupBy?: GroupBy | null
      order?: QueryOrder | null
      paranoid?: boolean
    },
    customWhere: SQLFragment<unknown> | null = null
  ): SQLFragment<WorldAttributes> => SQL`
        FROM ${WorldQ}
        ${includeAssociationsToSql(WorldQ, include)}
        WHERE
            ${id != null ? SQL`${WorldQ.id} = ${id} AND` : SQL``}
            ${courseId != null ? SQL`${WorldQ.course_id} = ${courseId} AND` : SQL``}
            ${slug != null ? SQL`${WorldQ.slug} = ${slug} AND` : SQL``}
            ${visibility != null ? SQL`${WorldQ.visibility} = ${visibility} AND` : SQL``}
            ${paranoid ? SQL`${WorldQ.deleted_at} IS NULL AND` : SQL``}
            ${customWhere != null ? customWhere : SQL`TRUE`}
        ${queryGroupByToSQL(groupBy)}
        ${queryOrderToSQL(order)}
        ${queryLimitToSQL(limit)}
    `,

  isUserMember: (userId: number | SQLFragment<number>): SQLFragment<boolean> =>
    SQL`EXISTS(SELECT 1 ${WorldMemberQ.where({ worldId: WorldQ.id, userId })})`,

  hasVisibility: (visibility: WorldVisibility): SQLFragment<boolean> =>
    SQL`${WorldQ.visibility} = ${visibility}`,

  isVisible: (): SQLFragment<boolean> => SQL`${WorldQ.visibility} = ANY(${VISIBLE_WORLD_TYPES})`,

  countMembers: (): SQLFragment<number> =>
    SQL`COUNT(*) ${WorldMemberQ.where({ worldId: WorldQ.id })}`,

  hasAnySlug: (slugs: string[]): SQLFragment<boolean> =>
    SQL`${WorldQ.slug} = ANY(${slugs}::text[])`,

  countPosts: (): SQLFragment<number> =>
    SQL`COUNT(*) ${PostQ.where(
      {
        worldId: WorldQ.id,
      },
      SQL`${PostQ.type} = ANY(${EDITABLE_POST_TYPES})`
    )}`,

  getSessionTemplates: ({
    userId,
  }: {
    userId: number | SQLFragment<number> | null
  }): SQLFragment<SessionTemplateAttributes> =>
    SessionTemplateQ.where(
      {
        active: true,
        include: [
          {
            association: 'User',
            required: true,
          },
          {
            association: 'Topic',
            required: true,
          },
        ],
      },
      SQL`(${SessionTemplateQ.world_id} IS NULL OR ${SessionTemplateQ.world_id} = ${WorldQ.id})
      AND ${SessionTemplateQ.series_template_id} IS NULL
      AND ${userId == null ? SQL`TRUE` : SQL`${TopicQ.canUserTutor(userId)}`}`
    ),

  countSessionTemplates: ({
    userId,
  }: {
    userId: number | SQLFragment<number> | null
  }): SQLFragment<number> => SQL`COUNT(*) ${WorldQ.getSessionTemplates({ userId })}`,

  getSeriesTemplates: (
    {
      userId,
      includeInactive = false,
    }: {
      userId?: number | SQLFragment<number> | null
      includeInactive?: boolean
    },
    where: SQLFragment<unknown> | null = null
  ): SQLFragment<SeriesTemplateAttributes> =>
    SeriesTemplateQ.where(
      {
        enabled: true,
        include: [
          {
            association: 'User',
            required: true,
          },
          {
            association: 'Course',
            required: true,
          },
          {
            association: 'Training',
            required: false,
          },
        ],
      },
      SQL`(${SeriesTemplateQ.world_id} IS NULL OR ${SeriesTemplateQ.world_id} = ${WorldQ.id})
      AND ${userId == null ? SQL`TRUE` : SeriesTemplateQ.canUserTutor({ userId })}
      AND ${includeInactive ? SQL`TRUE` : SQL`${SeriesTemplateQ.active} = TRUE`}
      AND ${where != null ? where : SQL`TRUE`}`
    ),

  countSeriesTemplates: ({
    userId,
  }: {
    userId: number | SQLFragment<number> | null
  }): SQLFragment<number> => SQL`COUNT(*) ${WorldQ.getSeriesTemplates({ userId })}`,

  isCommunity: (): SQLFragment<boolean> => SQL`${WorldQ.slug} = ANY(${COMMUNITY_WORLD_SLUGS})`,
  isAcademic: (): SQLFragment<boolean> => SQL`${WorldQ.slug} != ALL(${COMMUNITY_WORLD_SLUGS})`,
  isProgram: (): SQLFragment<boolean> => SQL`${WorldQ.slug} = ANY(${[...PROGRAM_SUBWORLD_SLUGS]})`,

  hasLiveHelp: (): SQLFragment<boolean> =>
    SQL`EXISTS(SELECT 1 ${ChannelQ.where({ worldId: WorldQ.id, type: 'question' })})`,
}

export default WorldQ
